/**
 * Servizio pagina richiesta OSPITE (/richiesta/:id).
 * L'unica credenziale è il token HMAC che arriva nel link email: viaggia
 * nell'header `X-Guest-Token` e NON viene mai salvato (no localStorage/cookie),
 * resta solo nella query string della pagina aperta dall'utente.
 */

import axios from 'axios';

// REST: usare REACT_APP_API_BASE_URL (include già /api), NON REACT_APP_API_URL (GraphQL).
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const auth = (t: string) => ({ 'X-Guest-Token': t });

// --- Tipi ---

export interface GuestThreadMessage {
  authorType: 'guest' | 'associate' | 'user' | 'representative';
  authorName?: string;
  body: string;
  sentAt: string;
  attachments?: { url: string; filename: string }[];
}

export interface GuestTicketDetail {
  _id: string;
  ticketNumber: string;
  subject: string;
  status: string;
  priority: string;
  canale?: string;
  createdAt?: string;
  publicThread: GuestThreadMessage[];
  rating?: { stars: number; comment?: string };
  resolvedAt?: string;
  closedAt?: string;
}

// --- Funzioni ---

export const getGuestTicket = async (id: string, t: string): Promise<GuestTicketDetail> => {
  const response = await axios.get(`${API_BASE_URL}/guest-tickets/${id}`, { headers: auth(t) });
  return response.data.data;
};

export const replyGuest = async (id: string, t: string, body: string, files?: File[]): Promise<void> => {
  let payload: any = { body };
  if (files && files.length > 0) {
    const fd = new FormData();
    fd.append('body', body);
    files.forEach((f) => fd.append('files', f));
    payload = fd;
  }
  // Con FormData non impostiamo Content-Type: axios aggiunge il boundary corretto.
  await axios.post(`${API_BASE_URL}/guest-tickets/${id}/messages`, payload, { headers: auth(t) });
};

export const rateGuest = async (id: string, t: string, stars: number, comment?: string): Promise<void> => {
  await axios.post(`${API_BASE_URL}/guest-tickets/${id}/rating`, { stars, comment }, { headers: auth(t) });
};

export const reopenGuest = async (id: string, t: string): Promise<void> => {
  await axios.post(`${API_BASE_URL}/guest-tickets/${id}/reopen`, {}, { headers: auth(t) });
};

/**
 * Scarica un allegato del thread pubblico e lo apre in una nuova scheda.
 * `storedName` è il nome file salvato (att.url), `displayName` l'originale.
 */
export const openGuestAttachment = async (
  id: string,
  t: string,
  storedName: string,
  displayName?: string
): Promise<void> => {
  // Apri la tab SINCRONAMENTE (dentro il gesto utente) per non farla bloccare dal
  // popup blocker; poi naviga al blob una volta scaricato.
  const win = window.open('', '_blank');
  try {
    const resp = await axios.get(
      `${API_BASE_URL}/guest-tickets/${id}/attachments/${encodeURIComponent(storedName)}`,
      { headers: auth(t), responseType: 'blob' }
    );
    const url = URL.createObjectURL(resp.data as Blob);
    if (win) {
      win.location.href = url;
    } else {
      const a = document.createElement('a');
      a.href = url;
      a.download = displayName || storedName;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (win) win.close();
    throw err;
  }
};
